import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getDelegationStats } from '../../../services/connectorsApi';

/**
 * DelegationStatsProvider — app-wide cached delegation stats.
 *
 * Same pattern as ApiKeyManagerProvider: fetch once, share across the
 * Connectors tree. Exposes `useDelegationStats()` returning
 * { stats, loading, hasLoadedOnce, refresh }.
 *
 * Components that need to know "is OpenClaw connected? how many tasks?"
 * read from here rather than refetching per-page.
 */

const EMPTY_STATS = {
  openclaw:    { connected: false, task_count: 0 },
  linear:      { connected: false, task_count: 0, installation_count: 0 },
  cursor:      { connected: false, task_count: 0 },
  claude_code: { connected: false, task_count: 0 },
  other:       { connected: false, task_count: 0, agent_names: [] },
  // CellCog's own remote MCP server (CEL-2750): chats created over MCP carry agent_name "mcp:<client>".
  // Backward compatible: a response without this bucket keeps the tile at "Not connected".
  mcp:         { connected: false, task_count: 0 },
  // Delegate v3 (CEL-3067, Oct 2 2026): the grid's green dots for Codex, Hermes and Muse.
  codex:       { connected: false, task_count: 0 },
  hermes:      { connected: false, task_count: 0 },
  muse:        { connected: false, task_count: 0 },
};

const DelegationStatsContext = createContext(null);

export const DelegationStatsProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const refresh = useCallback(async () => {
    if (!currentUser) {
      setStats(EMPTY_STATS);
      return;
    }
    try {
      setLoading(true);
      const { data } = await getDelegationStats();
      // Merge with EMPTY_STATS so partial responses never break consumers
      setStats({
        ...EMPTY_STATS,
        ...(data || {}),
      });
      setHasLoadedOnce(true);
    } catch (err) {
      // Silent fail — UI degrades gracefully to the empty-state
      // (everything shows as "Not connected")
      console.error('Failed to fetch delegation stats:', err);
      setStats(EMPTY_STATS);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) refresh();
    else setStats(EMPTY_STATS);
  }, [currentUser, refresh]);

  const value = useMemo(
    () => ({ stats, loading, hasLoadedOnce, refresh }),
    [stats, loading, hasLoadedOnce, refresh],
  );

  return (
    <DelegationStatsContext.Provider value={value}>
      {children}
    </DelegationStatsContext.Provider>
  );
};

/**
 * Hook for any Connectors component to read delegation stats.
 *
 * Returns { stats, loading, hasLoadedOnce, refresh }. Safe to call even
 * when the provider isn't mounted — returns empty defaults in that case
 * so consumers outside the provider (e.g., rare test harnesses) don't crash.
 */
export const useDelegationStats = () => {
  const ctx = useContext(DelegationStatsContext);
  if (!ctx) {
    // Graceful fallback — empty stats, no-op refresh
    return {
      stats: EMPTY_STATS,
      loading: false,
      hasLoadedOnce: false,
      refresh: () => Promise.resolve(),
    };
  }
  return ctx;
};
